<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    actual: number;
    target: number;
    unit?: string;
    formatValue?: (value: number) => string;
    /**
     * How the fill is coloured.
     *
     * - `'auto'` (default) reads the ratio as ACTUAL AGAINST A TARGET: nearing it
     *   warns, exceeding it is an error. Right for a budget — hours booked against
     *   planned.
     * - `'scale'` reads it as a POSITION ON A SCALE where low is bad and high is
     *   good, and fills from the ramp red → orange → yellow → blue → green (see
     *   `SCALE_STOPS`). Right for a rating — the personnel screen's "Niveau"
     *   column, where 100% is the best value and not an overrun.
     * - a literal variant pins the colour outright.
     *
     * The two readings are opposites, which is why this cannot be inferred: 100%
     * is an alarm for a budget and the ideal for a rating.
     */
    variant?: 'auto' | 'scale' | 'ok' | 'warn' | 'over' | 'neutral';
    /**
     * Accessible name of the bar. With it the bar is a `progressbar` that
     * announces the ratio („1,2 GB / 5 GB“); without it the bar is decoration
     * next to the visible ratio text (the default, e.g. in dense table cells).
     */
    label?: string;
    /**
     * Show the „actual / target“ line above the bar (default). Off for a caller
     * that writes its own text around the bar (NxUsageMeter).
     */
    showRatio?: boolean;
  }>(),
  {
    unit: 'h',
    formatValue: undefined,
    variant: 'auto',
    label: undefined,
    showRatio: true,
  }
);

const pct = computed(() =>
  props.target > 0 ? Math.min(100, (props.actual / props.target) * 100) : 0
);

const fillVariant = computed(() => {
  if (props.target <= 0) return 'neutral';
  const ratio = props.actual / props.target;
  if (props.variant === 'scale') {
    // Low is bad, high is good — the inverse of the budget reading below. These
    // three bands are only the FALLBACK for the continuous ramp in `scaleFill`:
    // a browser without color-mix() drops that declaration and lands here.
    if (ratio <= 1 / 3) return 'over';
    if (ratio <= 2 / 3) return 'warn';
    return 'ok';
  }
  if (props.variant !== 'auto') return props.variant;
  if (ratio > 1) return 'over';
  if (ratio >= 0.85) return 'warn';
  return 'ok';
});

/**
 * Stops of the `scale` ramp, evenly spread over 0…100%:
 * red → orange → yellow → blue → green (maintainer's requirement, 2026-08-04).
 *
 * Taken from the THEME TOKENS, not from invented colour values — that way the
 * ramp follows the palette and the theme. Hue and chroma therefore come from the
 * respective token; there is none for yellow (the palette only knows
 * danger/warning/info/success), so it is the orange token rotated to 95°.
 *
 * THE LIGHTNESS IS NORMALISED rather than taken from the tokens: those are made
 * for surfaces and text and lie far apart — the brand blue is a dark navy
 * (L ≈ 0.36). In a ramp that shows up as a jump, some steps disappear while
 * others push forward. Normalised, only the HUE carries the step, which is
 * exactly what it should do here.
 *
 * 0.62 as the base value is not arbitrary but the lightness anchor the palette
 * itself uses for its hover shades (--nx-*-hover in theme/color/color.css); the
 * same `oklch(from …)` notation appears there too.
 *
 * THREE DEVIATIONS, each with a reason:
 *   orange 0.70/0.19  --nx-warning is a muted amber (#b45309), built as a
 *                     background for text. At that chroma a colour surface reads
 *                     as rust brown, not as orange — so chroma and lightness go
 *                     up and the hue gets a touch warmer.
 *   yellow 0.80/0.17  Yellow has a high INTRINSIC lightness — pulled down to
 *                     0.62 it turns into ochre, i.e. visibly brown instead of
 *                     yellow. It has to sit brighter to read as yellow; chroma
 *                     raised as well, otherwise it looks washed out.
 *   blue   0.58/0.16  --nx-info is the brand colour and heavily desaturated
 *                     (chroma 0.09) — as a colour surface it looks grey-blue. So
 *                     chroma clearly up, lightness just below the base value:
 *                     strong, but not a dark navy (maintainer's wish,
 *                     2026-08-04). The hue stays the brand's.
 *   green  token pure --nx-success unchanged, i.e. exactly the green of the
 *                     "active" chip in the mask header (maintainer's wish,
 *                     2026-08-04). That is more than taste: the same green
 *                     should mean the same thing in the mask. Normalising would
 *                     have darkened it.
 */
const SCALE_LIGHTNESS = 0.62;
const SCALE_STOPS = [
  `oklch(from var(--nx-danger) ${SCALE_LIGHTNESS} c h)`, // red
  'oklch(from var(--nx-warning) 0.7 0.19 55)', // orange
  'oklch(from var(--nx-warning) 0.8 0.17 95)', // yellow — no token of its own
  'oklch(from var(--nx-info) 0.58 0.16 h)', // blue (the brand's hue)
  'var(--nx-success)', // green — token pure, like the "active" chip
];

/**
 * Fill colour for `variant="scale"`; otherwise undefined (then the variant class
 * does the colouring).
 *
 * Mixing happens between two neighbouring stops, `in oklch` — perceptually even
 * and around the colour wheel; an sRGB mix of red and green runs through a muddy
 * brown. With four steps (25/50/75/100) every step hits exactly one stop, so
 * mixing only occurs for scales with a different number of steps.
 *
 * If a browser cannot do color-mix(), it discards the declaration and the three
 * fallback bands from `fillVariant` take over.
 */
const scaleFill = computed(() => {
  if (props.variant !== 'scale' || props.target <= 0) return undefined;
  const ratio = Math.min(1, Math.max(0, props.actual / props.target));
  const segments = SCALE_STOPS.length - 1;
  const position = ratio * segments;
  // At ratio = 1 `position` sits on the last stop; take the last segment and mix
  // it fully instead of reaching past the end of the array.
  const index = Math.min(segments - 1, Math.floor(position));
  const share = (position - index) * 100;
  return `color-mix(in oklch, ${SCALE_STOPS[index]}, ${SCALE_STOPS[index + 1]} ${share}%)`;
});

function fmt(value: number): string {
  if (props.formatValue) return props.formatValue(value);
  return value.toLocaleString('de-DE', { maximumFractionDigits: 1 });
}
</script>

<template>
  <div class="nx-progress-meter">
    <div v-if="showRatio" class="nx-progress-meter-ratio">
      <span class="nx-progress-meter-actual">{{ fmt(actual) }}</span>
      <span class="nx-progress-meter-target">{{ `/ ${fmt(target)}${unit}` }}</span>
    </div>
    <div
      class="nx-progress-meter-bar"
      :role="label ? 'progressbar' : undefined"
      :aria-label="label || undefined"
      :aria-valuemin="label ? 0 : undefined"
      :aria-valuemax="label ? Math.max(0, target) : undefined"
      :aria-valuenow="label ? Math.min(Math.max(0, actual), Math.max(0, target)) : undefined"
      :aria-valuetext="label ? `${fmt(actual)} / ${fmt(target)}${unit}` : undefined"
      :aria-hidden="label ? undefined : 'true'"
    >
      <div
        class="nx-progress-meter-fill"
        :class="`variant-${fillVariant}`"
        :style="{ width: `${pct}%`, background: scaleFill }"
      />
    </div>
  </div>
</template>

<style scoped>
/* NxProgressMeter consumes the --nxt-progress-* component tokens (I-596).
   The track/fill read their own tokens directly; each fill variant re-points
   --nxt-progress-fill at the matching semantic role, mirroring the
   NxButton/NxBadge pilots. */
.nx-progress-meter {
  font-variant-numeric: tabular-nums;
  font-size: var(--nxt-font-size-xs);
}

.nx-progress-meter-ratio {
  display: flex;
  align-items: baseline;
  gap: var(--nxt-space-2xs);
  color: var(--nxt-fg-muted);
}

.nx-progress-meter-actual {
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-semibold);
}

.nx-progress-meter-target {
  color: var(--nxt-progress-fg);
}

/* A rounded bar on a track you can see (the chart track, like NxProgressBar);
   `em`-sized so it stays slim in dense table rows. */
.nx-progress-meter-bar {
  height: 0.5em;
  background: var(--nxt-chart-track, var(--nxt-progress-track-bg));
  border-radius: var(--nxt-radius-full);
  margin-top: calc(var(--nxt-space-2xs) * 1.5);
  overflow: hidden;
}
/* Without the ratio line the bar is the first row: no gap above it. */
.nx-progress-meter-bar:first-child {
  margin-top: 0;
}

.nx-progress-meter-fill {
  height: 100%;
  background: var(--nxt-progress-fill);
  border-radius: var(--nxt-radius-full);
  transition: width var(--nxt-motion-fast) var(--nxt-ease-out);
}

@media (prefers-reduced-motion: reduce) {
  .nx-progress-meter-fill {
    transition: none;
  }
}

@media (forced-colors: active) {
  .nx-progress-meter-bar {
    border: var(--nxt-hairline) solid CanvasText;
  }
  .nx-progress-meter-fill {
    background: Highlight;
  }
}

.nx-progress-meter-fill.variant-ok {
  --nxt-progress-fill: var(--nxt-success-bg);
}

.nx-progress-meter-fill.variant-warn {
  --nxt-progress-fill: var(--nxt-warning-bg);
}

.nx-progress-meter-fill.variant-over {
  --nxt-progress-fill: var(--nxt-danger-bg);
}

.nx-progress-meter-fill.variant-neutral {
  /* should always be width = 0% */
  --nxt-progress-fill: var(--nxt-progress-track-bg);
}
</style>
